"use client";

import { useEffect } from "react";

type TocItem = {
  id: string;
  label: string;
};

type TableOfContentsProps = {
  title: string;
  items: TocItem[];
  locale: string;
};

const TableOfContents = ({ title, items, locale }: TableOfContentsProps) => {
  const isRtl = locale === "ar";

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (!el) return;

    const scrollTimeout = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    return () => clearTimeout(scrollTimeout);
  }, []);

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "start" });

    window.history.pushState(null, "", `#${id}`);
  };

  return (
    <nav aria-label={title} className="inter my-6 rounded-4xl border bg-white px-6 py-5" dir={isRtl ? "rtl" : "ltr"}>
      <p className="mb-3 font-urbanist text-base font-semibold text-primary-foreground">{title}</p>
      <ol className="space-y-2">
        {items.map((item, index) => (
          <li key={item.id} className="flex items-start gap-2">
            <span className="urbanist mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-primary">
              {index + 1}
            </span>
            <a
              href={`#${item.id}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleAnchorClick(e, item.id)}
              className="font-inter text-sm text-primary-foreground underline underline-offset-2 hover:underline focus:underline focus:outline-none"
              tabIndex={0}
              aria-label={item.label}
            >
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default TableOfContents;
